import Vue from 'vue';
import OrdersService from '@/services/orders';
import {
	SET_ORDER,
	CLEAR_ORDER,
	CLEAR_BASKET,
	SET_BASKET_ITEM,
	SET_BASKET_COUNT,
	UPDATE_BASKET_ITEM,
	DELETE_BASKET_ITEM,
	SET_ORDER_RECIPIENT,
	SET_BACKEND_ORDER_ID,
	CLEAR_BACKEND_ORDER_ID,
	CLEAR_ORDER_INFORMATION,
	SET_ORDER_PAYMENT_DETAILS
} from '../mutation-types';

const state = {
	items: JSON.parse(localStorage.getItem('basket_items')) || {},
	order: localStorage.getItem('order') || null,
	count: localStorage.getItem('basket_count') || 0,
	recipient: JSON.parse(localStorage.getItem('recipient')) || {},
	payment_details: JSON.parse(localStorage.getItem('payment_details')) || {},
	backend_order_id: sessionStorage.getItem('backend_order_id') || null
};

const getters = {
	getItems(state) {
		return state.items;
	},

	getCount(state) {
		return state.count;
	},

	getRecipient(state) {
		return state.recipient;
	},

	getPaymentDetails(state) {
		return state.payment_details;
	},

	getOrder(state) {
		let items = {};

		if (state.order) {
			items[state.order] = state.items[state.order];

			return items;
		}
		
		return state.items;
	},

	getBackendOrderId(state) {
		return state.backend_order_id;
	}
};

const mutations = {
	[SET_BASKET_ITEM] (state, payload) {
		let index = payload.menuItem.restaurant.id;

		if (typeof state.items[index] === 'undefined') {
			Vue.set(state.items, index, []);
		}
		
		state.items[index].push({
			key: payload.key,
			details: payload.details,
			menuItem: payload.menuItem
		});

		localStorage.setItem('basket_items', JSON.stringify(state.items));
	},

	[UPDATE_BASKET_ITEM] (state, payload) {
		let restaurant = state.items[payload.menuItem.restaurant.id];
		let item;

		if (typeof restaurant === 'undefined') {
			return;
		}

		item = restaurant[payload.index];

		if (typeof item === 'undefined') {
			return;
		}

		Vue.set(state.items[payload.menuItem.restaurant.id], 
			payload.index, 
			Object.assign({}, 
				state.items[payload.menuItem.restaurant.id][payload.index], {
					details: payload.details,
					menuItem: payload.menuItem
				})
		);

		localStorage.setItem('basket_items', JSON.stringify(state.items));
	},

	[DELETE_BASKET_ITEM] (state, payload) {
		let restaurant = state.items[payload.restaurantIndex];
		let item;

		if (typeof restaurant === 'undefined') {
			return;
		}

		item = restaurant[payload.itemIndex];

		if (typeof item === 'undefined') {
			return;
		}

		// If the item is the last element, delete the whole restaurant row.
		if (restaurant.length === 1) {
			Vue.delete(state.items, payload.restaurantIndex);
		} else {
			Vue.delete(state.items[payload.restaurantIndex], payload.itemIndex);
		}

		localStorage.setItem('basket_items', JSON.stringify(state.items));
	},

	[SET_BASKET_COUNT] (state) {
		let totalItems = 0;
	
		for (let restaurant of Object.values(state.items)) {
			for (let item of restaurant) {
				let quantity = item.details.quantity;

				quantity = isNaN(Number(quantity)) ? 0 : Number(quantity);		
				totalItems += quantity;
			}
		}

		state.count = totalItems;
		localStorage.setItem('basket_count', state.count);
	},

	[CLEAR_BASKET] (state) {
		state.items = {};
		state.count = 0;

		localStorage.removeItem('basket_items');
		localStorage.removeItem('basket_count');
	},

	[SET_ORDER_RECIPIENT] (state, payload) {
		state.recipient = payload;
		localStorage.setItem('recipient', JSON.stringify(state.recipient));
	},

	[SET_ORDER_PAYMENT_DETAILS] (state, payload) {
		state.payment_details = payload;
		localStorage.setItem('payment_details', JSON.stringify(state.payment_details));
	},

	[SET_ORDER] (state, payload) {
		state.order = payload;
		localStorage.setItem('order', state.order);
	},

	[CLEAR_ORDER] (state) {
		state.order = null;
		localStorage.removeItem('order');
	},

	[SET_BACKEND_ORDER_ID] (state, payload) {
		state.backend_order_id = payload;
		sessionStorage.setItem('backend_order_id', state.backend_order_id);
	},
	
	[CLEAR_BACKEND_ORDER_ID] (state) {
		state.backend_order_id = null;
		sessionStorage.removeItem('backend_order_id');
	},

	/**
	 * When an order is processed successfully,
	 * clear all the information of the order from the state.
	 */
	[CLEAR_ORDER_INFORMATION] (state) {
		// Clear backend order id
		state.backend_order_id = null;
		sessionStorage.removeItem('backend_order_id');

		// Remove only the order checked out
		if (state.order) {
			Vue.delete(state.items, state.order);
		} else {
			// Remove all items
			state.items = {};
		}

		localStorage.setItem('basket_items', JSON.stringify(state.items));

		// Clear order
		state.order = null;
		localStorage.removeItem('order');
	}
};

const actions = {
	all({ commit }, query) {
		return MenuItemsService.all(query)
			.then((response) => {
				commit(SET_MENU_ITEMS, response);

				return Promise.resolve(response);
			})
			.catch((error) => Promise.reject(error));
	},

	add({ commit }, payload) {
		commit(SET_BASKET_ITEM, payload);
		commit(SET_BASKET_COUNT);
	},

	update({ commit }, payload) {
		commit(UPDATE_BASKET_ITEM, payload);
		commit(SET_BASKET_COUNT);
	},

	delete({ commit }, payload) {
		commit(DELETE_BASKET_ITEM, payload);
		commit(SET_BASKET_COUNT);
	},

	clear({ commit }) {
		commit(CLEAR_BASKET);
		commit(SET_BASKET_COUNT);
	},

	setRecipient({ commit }, payload) {
		commit(SET_ORDER_RECIPIENT, payload);
	},

	setPaymentDetails({ commit }, payload) {
		commit(SET_ORDER_PAYMENT_DETAILS, payload);
	},

	setOrder({ commit }, payload) {
		commit(SET_ORDER, payload);
	},	

	clearOrder({ commit }) {
		commit(CLEAR_ORDER);
	},

	/**
	 * Check the status of an order's payment.
	 * 
	 * @param  {Function} options.commit
	 * @param  {Object} payload       
	 * @return {Object}               
	 */
	checkPayment({ commit }, payload) {
		return OrdersService.check(payload)
			.then((response) => {
				if (response.errors) {
					commit(CLEAR_BACKEND_ORDER_ID);
				}
					
				if (response.status && response.status.toLowerCase() === 'success') {
					commit(CLEAR_ORDER_INFORMATION);
					commit(SET_BASKET_COUNT);
				}

				return Promise.resolve(response);
			})
			.catch((error) => Promise.reject(error));
	},

	/**
	 * Finalise the payment of an order.
	 * 
	 * @param  {Function} options.commit
	 * @param  {Object} payload       
	 * @return {Object}               
	 */
	payNow({ commit }, payload) {
		return OrdersService.store(payload)
			.then((response) => {
				if (response.payment_status && response.payment_status.toLowerCase() === 'processed') {
					commit(CLEAR_ORDER_INFORMATION);
					commit(SET_BASKET_COUNT);
				} else {
					commit(SET_BACKEND_ORDER_ID, response.order_id);
				}
				
				return Promise.resolve(response);
			})
			.catch((error) => Promise.reject(error));
	}
};

const namespaced = true;

export default {
	namespaced,
	state,
	actions,
	getters,
	mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/basket.js